import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { filter, firstValueFrom, take } from 'rxjs';
import { DialogService } from '../shared/dialogs/dialog.service';
import { PageWithSplashComponent } from '../shared/page-templates/page-with-splash/page-with-splash.component';
import { AuthenticationService } from './authentication.service';
import { email, form, FormField, required, schema, submit } from '@angular/forms/signals';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { getApiErrorMessage } from '../core/mceapi.client.helpers';

export interface SignInFormModel {
  email: string;
}

export const signInFormSchema = schema<SignInFormModel>((path) => {
  required(path.email, { message: `Email is required` });
  email(path.email, { message: `Please enter a valid email address` });
});

@Component({
    selector: 'authentication-signin',
  imports: [FormField, MatFormFieldModule, MatInputModule, MatProgressSpinnerModule,
    PageWithSplashComponent, MatIconModule, MatDividerModule, MatButtonModule],
    templateUrl: './signin.component.html',
})
export class SigninComponent {

  private readonly authenticationService = inject(AuthenticationService);
  private readonly dialogService = inject(DialogService);
  private readonly router = inject(Router);

  readonly signInModel = signal<SignInFormModel>({ email: '' });
  readonly signInForm = form<SignInFormModel>(this.signInModel, signInFormSchema);
  readonly googleSignInLoading = signal(false); 

  submitForm(event: Event) {
    event.preventDefault();
    submit(this.signInForm, async (form) => {

      try {
        const result = await this.authenticationService.sendSignInLink(form().value().email);

        if (result === 'login-disabled') {
          await firstValueFrom(this.dialogService.acknowledge(
            'Login Disabled',
            'This user is not enabled for logging in.',
            '320px'));
          return undefined;
        }

        await firstValueFrom(this.dialogService.acknowledge(
          'Login Link Sent',
          `We’ve sent a sign-in link to ${form().value().email}.
              If it does not arrive within a few minutes, check your spam/junk folder or go back and confirm the email address.`, '320px'))
        return undefined;
      }

      catch (error) {
        const errorMessage = getApiErrorMessage(error);
        await firstValueFrom(this.dialogService.acknowledge(
          'Login Link Error',
          'Error retrieving sign in link: ' + errorMessage));
        return [{
          message: errorMessage,
          kind: 'unexpected'
        }];

      }
    })
  }

  async signInWithGoogle() {
    this.googleSignInLoading.set(true);
    try {
      const result = await firstValueFrom(this.authenticationService.signInWithGoogle());

      if (result === 'login-disabled') {
        await firstValueFrom(this.dialogService.acknowledge(
          'Login Disabled',
          'This user is not enabled for logging in.',
          '320px'));
        return;
      }

      // Wait for initialization
      await firstValueFrom(
        this.authenticationService.initialized$.pipe(
          filter(Boolean),
          take(1)
        )
      );

      // Navigate to dashboard
      this.router.navigate(['/dashboard']);
    } catch (error) {
      console.error('Google sign-in failed', error);
      // Error dialog already shown by service
    } finally {
      // Always reset loading state
      this.googleSignInLoading.set(false);
    }
  }

}
